import type { DocumentoCadastroParte } from "@atlas/sdk";
import { useEffect, useRef, useState } from "react";

// Um anexo do cadastro do site institucional (documento frente/verso ou selfie).
//
// Por que blob e nao <img src="/v1/admin/..."> direto: a rota exige
// Authorization: Bearer e a tag <img> nao manda header. Mesmo caminho do
// download da CCB na carteira do banco (lib/sdk fetchCcbBlob).

/** Miniatura clicavel + lightbox. `parte` existe pra dar identidade estavel ao
 *  efeito — ver a nota no useEffect. */
export function DocumentoPreview({
  label,
  parte,
  carregar,
}: {
  label: string;
  parte: DocumentoCadastroParte;
  carregar: (parte: DocumentoCadastroParte) => Promise<Blob>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [tipo, setTipo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ampliado, setAmpliado] = useState(false);
  // A funcao vive numa ref pro efeito NAO depender dela. O pai monta
  // `carregar` como arrow inline, entao a identidade muda a cada render dele —
  // com `carregar` nas deps, qualquer re-render do modal refazia o download e
  // revogava a object URL que o <img> ainda estava usando (imagem piscando e
  // request duplicado). A ref se mantem atual sem disparar o efeito.
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;

  useEffect(() => {
    let objectUrl: string | null = null;
    let vivo = true;
    carregarRef.current(parte)
      .then((blob) => {
        if (!vivo) return;
        objectUrl = URL.createObjectURL(blob);
        setTipo(blob.type);
        setUrl(objectUrl);
      })
      .catch(() => { if (vivo) setErro("Não foi possível carregar."); });
    return () => {
      vivo = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [parte]);

  if (erro) {
    return (
      <div>
        <div style={legenda}>{label}</div>
        <div style={{ ...caixa, color: "var(--danger-500)" }}>{erro}</div>
      </div>
    );
  }
  if (!url) {
    return (
      <div>
        <div style={legenda}>{label}</div>
        <div style={{ ...caixa, color: "var(--text-muted)" }}>carregando…</div>
      </div>
    );
  }
  // PDF nao tem miniatura util — abre em aba nova. A object URL do state nao
  // pode ser usada ali: o unmount deste componente a revoga e a aba fica em
  // branco. Uma URL nova, nao revogada, sobrevive enquanto a aba estiver aberta.
  if (tipo === "application/pdf") {
    return (
      <div>
        <div style={legenda}>{label}</div>
        <button
          type="button"
          onClick={() => fetch(url).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank", "noopener"))}
          style={{ ...caixa, cursor: "pointer", color: "var(--accent)", textAlign: "left", width: "100%", font: "inherit", fontSize: 12 }}
        >
          📄 Abrir PDF em nova aba
        </button>
      </div>
    );
  }
  return (
    <div>
      <div style={legenda}>{label}</div>
      {/* A miniatura cabe em ~290px (modal de 640px, grid de 2). Ilegivel pra
          conferir um documento de identidade, por isso o clique amplia. */}
      <button
        type="button"
        onClick={() => setAmpliado(true)}
        title="Clique para ampliar"
        style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in", display: "block", width: "100%" }}
      >
        <img src={url} alt={label} style={{ width: "100%", display: "block", borderRadius: 8, border: "1px solid var(--border)" }} />
      </button>
      {ampliado ? (
        // zIndex acima do backdrop do modal que hospeda este componente (100).
        <div
          onMouseDown={() => setAmpliado(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,22,40,.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24, cursor: "zoom-out", backdropFilter: "blur(4px)",
          }}
        >
          <img
            src={url}
            alt={label}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8, boxShadow: "var(--shadow-lg)" }}
          />
        </div>
      ) : null}
    </div>
  );
}

const legenda: React.CSSProperties = {
  fontSize: 11, color: "var(--text-muted)", marginBottom: 4,
};
const caixa: React.CSSProperties = {
  fontSize: 12, padding: "10px 12px", borderRadius: 8,
  border: "1px dashed var(--border)", background: "var(--bg-elev)",
};
